import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ScanFace, CheckCircle2, XCircle, Clock, Upload, Trash2,
  Search, Building2, ShieldCheck, AlertTriangle,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import {
  listEnrollments, uploadEnrollmentPhoto, submitEnrollment, deleteEnrollment,
} from "../../api/face";
import { listBranches } from "../../api/branches";
import { unwrapList, errorMessage, formatDate } from "../../lib/records";
import { Button, Avatar } from "../../components/ui";

const QUALITY_WARN = 60;

function QualityDot({ value }) {
  if (value == null) return null;
  const good = value >= QUALITY_WARN;
  return (
    <span className={`inline-block h-2 w-2 rounded-full ${good ? "bg-emerald-400" : "bg-amber-400"}`} title={`Score: ${value}`} />
  );
}

function EnrollModal({ employee, onClose, onDone }) {
  const [step, setStep] = useState("upload"); // "upload" | "preview" | "saving"
  const [photoKey, setPhotoKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await uploadEnrollmentPhoto(employee._id, fd);
      const key = res?.data?.key ?? res?.key;
      if (!key) throw new Error("No photo key returned");
      setPhotoKey(key);
      setPreview(URL.createObjectURL(file));
      setStep("preview");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  async function confirm() {
    setStep("saving");
    setError("");
    try {
      await submitEnrollment(employee._id, { photoKey });
      toast.success(`${employee.name} enrolled successfully`);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setStep("preview");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-5 py-4">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <ScanFace size={18} /> Enrol Face — {employee.name}
          </h2>
          <p className="text-xs text-emerald-100 mt-0.5">Upload a clear, front-facing photo</p>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === "upload" && (
            <>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-emerald-300 rounded-xl p-8 text-center cursor-pointer hover:bg-emerald-50 transition"
              >
                {uploading ? (
                  <RefreshCw size={28} className="mx-auto text-emerald-400 animate-spin" />
                ) : (
                  <>
                    <Upload size={28} className="mx-auto text-emerald-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700">Click to choose photo</p>
                    <p className="text-xs text-gray-400 mt-1">JPG or PNG · good lighting · face centred</p>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => handleFile(e.target.files?.[0])}
              />
            </>
          )}

          {(step === "preview" || step === "saving") && preview && (
            <div className="text-center">
              <img
                src={preview}
                alt="Preview"
                className="mx-auto h-48 w-48 rounded-xl object-cover border-2 border-emerald-200 shadow"
              />
              <p className="text-xs text-gray-400 mt-2">Photo uploaded. Confirm to enrol.</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <Button variant="ghost" onClick={onClose} className="flex-1 text-gray-600 border border-gray-200">
            Cancel
          </Button>
          {step === "preview" && (
            <>
              <Button
                variant="ghost"
                onClick={() => { setStep("upload"); setPreview(null); setPhotoKey(""); }}
                className="border border-gray-200 text-sm"
              >
                Retake
              </Button>
              <Button
                onClick={confirm}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm"
              >
                <CheckCircle2 size={15} /> Confirm
              </Button>
            </>
          )}
          {step === "saving" && (
            <Button disabled className="flex-1 bg-emerald-400 text-white text-sm">
              Saving…
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function RevokeModal({ employee, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function confirm() {
    setSaving(true);
    try {
      await deleteEnrollment(employee._id, note.trim() ? { note: note.trim() } : {});
      toast.success(`Enrollment revoked for ${employee.name}`);
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <Trash2 size={18} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Revoke enrollment?</h2>
            <p className="text-xs text-gray-500">{employee.name}</p>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          The face template will be permanently erased. The employee will need to re-enrol before face verification works for them.
        </p>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Reason (optional)"
          rows={2}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400/30"
        />
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 border border-gray-200 text-gray-600">
            Cancel
          </Button>
          <Button
            onClick={confirm}
            loading={saving}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm"
          >
            Revoke
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function FaceEnrollments({ onSwitchView } = {}) {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [search, setSearch] = useState("");
  const [enrolledFilter, setEnrolledFilter] = useState("");

  const [enrollTarget, setEnrollTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  useEffect(() => {
    if (isChain) {
      listBranches({ limit: 100 }).then(r => setBranches(unwrapList(r).items)).catch(() => {});
    }
  }, [isChain]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (branchFilter) params.partner = branchFilter;
      if (search.trim()) params.search = search.trim();
      if (enrolledFilter !== "") params.enrolled = enrolledFilter === "true";
      const res = await listEnrollments(params);
      const { items: rows, pagination: pg } = unwrapList(res);
      setItems(rows);
      setPagination(pg);
      setSummary(res?.data?.summary ?? null);
      setPage(p);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [branchFilter, search, enrolledFilter]);

  useEffect(() => { load(1); }, [load]);

  function done(clearFn) { clearFn(); load(page); }

  return (
    <div className="space-y-5 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ScanFace size={22} className="text-emerald-600" />
            Face Enrollment
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage employee face templates for biometric attendance</p>
        </div>
        <Button
          variant="ghost"
          onClick={() => onSwitchView ? onSwitchView("verifications") : navigate("/face/verifications")}
          className="text-sm text-gray-600 border border-gray-200"
        >
          <ShieldCheck size={14} /> Verification queue
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "On this page", value: summary.onPage, color: "text-gray-700", bg: "bg-gray-50 border-gray-200" },
            { label: "Enrolled", value: summary.enrolled, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
            { label: "Consented", value: summary.consented, color: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
            { label: "Revoked", value: summary.revoked, color: "text-red-700", bg: "bg-red-50 border-red-200" },
          ].map(c => (
            <div key={c.label} className={`rounded-xl border p-3 ${c.bg}`}>
              <p className="text-xs text-gray-500 font-medium">{c.label}</p>
              <p className={`text-2xl font-bold tabular-nums mt-0.5 ${c.color}`}>{c.value ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-2">
        {isChain && branches.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Building2 size={14} className="text-gray-400" />
            <select
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
            >
              <option value="">All branches</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
          <Search size={14} className="text-gray-400 shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search employee…"
            className="text-sm outline-none w-36 text-gray-700"
          />
        </div>
        <select
          value={enrolledFilter}
          onChange={e => setEnrolledFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
        >
          <option value="">All employees</option>
          <option value="true">Enrolled only</option>
          <option value="false">Not enrolled</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-12 text-center">
          <ScanFace size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 text-sm">No employees found</p>
        </div>
      ) : (
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-50">
            {items.map(row => {
              const emp = row.employee ?? row;
              const enrolled = row.isEnrolled;
              const consented = row.hasConsent;
              return (
                <div key={emp._id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/60 transition">
                  <Avatar name={emp.name} size={36} className="shrink-0 bg-gradient-to-br from-emerald-500 to-green-600 text-white text-sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{emp.name}</span>
                      {emp.designation && <span className="text-xs text-gray-400">{emp.designation}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {enrolled ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                          <CheckCircle2 size={11} /> Enrolled
                          {row.enrolledAt && <span className="text-gray-400 font-normal">· {formatDate(row.enrolledAt)}</span>}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                          <XCircle size={11} /> Not enrolled
                        </span>
                      )}
                      {!consented && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <AlertTriangle size={10} /> No consent
                        </span>
                      )}
                      {enrolled && row.quality && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <QualityDot value={row.quality.sharpness} />
                          sharpness {Math.round(row.quality.sharpness)}
                        </span>
                      )}
                      {row.lastVerifiedAt && (
                        <span className="text-xs text-gray-400">
                          Last verified {formatDate(row.lastVerifiedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!enrolled && consented && (
                      <Button
                        onClick={() => setEnrollTarget(emp)}
                        className="bg-emerald-500 text-white text-xs px-3 py-1.5"
                      >
                        <Upload size={12} /> Enrol
                      </Button>
                    )}
                    {!consented && (
                      <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1">
                        Awaiting consent
                      </span>
                    )}
                    {enrolled && (
                      <button
                        onClick={() => setRevokeTarget(emp)}
                        className="rounded-lg p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition"
                        title="Revoke enrollment"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && (pagination.pages ?? 1) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{pagination.total} employees · page {page} of {pagination.pages}</p>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => load(page - 1)} className="text-sm border border-gray-200">Previous</Button>
            <Button variant="ghost" disabled={page >= pagination.pages} onClick={() => load(page + 1)} className="text-sm border border-gray-200">Next</Button>
          </div>
        </div>
      )}

      {enrollTarget && (
        <EnrollModal
          employee={enrollTarget}
          onClose={() => setEnrollTarget(null)}
          onDone={() => done(() => setEnrollTarget(null))}
        />
      )}
      {revokeTarget && (
        <RevokeModal
          employee={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onDone={() => done(() => setRevokeTarget(null))}
        />
      )}
    </div>
  );
}
